import { useAuthStore } from '@/store/authStore';
import {
  getPrimaryRole,
  hasPermission as hasPermissionByRole,
  hasAnyPermission as hasAnyPermissionByRole,
  hasAllPermissions as hasAllPermissionsByRole,
} from '@/utils/auth';
import type { OrganizationStatus, Permission, UserRole } from '@/types/models';

export function usePermissions() {
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const hasAllPermissions = useAuthStore((s) => s.hasAllPermissions);
  const roles = user?.roles ?? [];

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole: (role: string) => roles.includes(role),
    hasAnyRole: (requiredRoles: string[]) =>
      requiredRoles.some((role) => roles.includes(role)),
    isPlatformAdmin: roles.includes('platform_admin'),
    isOrgAdmin: roles.includes('org_admin') || roles.includes('manager'),
    user,
    organization: user
      ? {
          id: user.organizationId,
          name: 'Organization',
          status: user.organizationStatus ?? 'approved',
        }
      : null,
    isAuthenticated,
    isLoading,
  };
}

export function useOrganizationStatus() {
  const { user, isAuthenticated } = useAuthStore();
  const roles = user?.roles ?? [];
  const status = (user?.organizationStatus ?? 'approved') as OrganizationStatus;
  const isPlatformAdmin = roles.includes('platform_admin');
  const isPending = status === 'pending';
  const isSuspended = status === 'suspended';
  const isRejected = status === 'rejected';
  const canAccess =
    isPlatformAdmin || status === 'active' || status === 'approved';

  return {
    status,
    canAccess: isAuthenticated && canAccess,
    isPending,
    isSuspended,
    isRejected,
    isPlatformAdmin,
  };
}
